import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { errorMessage } from '../../components/ui/States';
import { useAccess } from '../../hooks/useAccess';
import { useSubscriptionsSummary, useInvoicesSummary } from '../../hooks/useFinance';
import { useMrrOverview } from '../../hooks/useDashboard';
import { useSalesMeetings, useSalesOverview } from '../../hooks/useProjectManagement';
import { useTaskCounts } from '../../hooks/useOrganizer';
import { useIntegrationExceptions } from '../../components/SystemDiagram';
import { useAutomations } from '../../hooks/useAutomations';
import { failureCause } from '../Integrations/syncError';
import { currentQuarter } from '../ProjectManagement/Sales/dealKinds';
import { addWeeks, mondayOf } from '../ProjectManagement/Sales/meetingsWeek';
import { formatMoneyCompact, formatMoney } from '../../utils/numberFormat';
import { formatDayMonth, formatMonthYearShort } from '../../utils/dateFormat';
import { RENEWAL_WINDOW_DAYS, contractsEnding, mrrMovementLine, overrideSummary } from './todayNumbers';
import { attentionCounts, attentionParts } from '../Automations/automationStatus';

/** "Oct": the month the month-scoped tiles describe. */
const thisMonth = () => formatMonthYearShort(new Date()).split(' ')[0];
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const signedMoney = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${formatMoney(Math.abs(Math.round(n)))}`;

type StatState = { loading: boolean; error: unknown };

/**
 * One compact stat that links to its page. Loading is a skeleton bar; a failed
 * query says "unavailable" with the error in the tooltip, never a blank.
 */
function TodayStat({
  label,
  meta,
  href,
  state,
  value,
  sub,
  title,
}: {
  label: string;
  /** Source and period, on its own line so the label is never cut: "Attio · today". */
  meta: string;
  href: string;
  state: StatState;
  value?: ReactNode;
  sub?: ReactNode;
  title?: string;
}) {
  const failed = !!state.error;
  return (
    <Link
      to={href}
      title={failed ? `Couldn’t load: ${errorMessage(state.error)}` : title}
      className="group min-w-0 rounded-lg px-3 py-2.5 -mx-1 transition-colors hover:bg-surface-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
    >
      <p className="eyebrow flex min-w-0 items-center gap-1">
        <span>{label}</span>
        <ArrowRight className="h-3 w-3 flex-none opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
      </p>
      <p className="text-body-xs text-text-muted">{meta}</p>
      {state.loading && !failed ? (
        <div className="mt-2 space-y-1.5" aria-label="Loading">
          <div className="h-6 w-20 rounded-sm bg-surface-tertiary animate-pulse" />
          <div className="h-3 w-14 rounded-sm bg-surface-tertiary animate-pulse" />
        </div>
      ) : failed ? (
        <>
          <p className="mt-2 text-body-md text-text-tertiary">Unavailable</p>
          <p className="text-body-xs text-error mt-0.5">Load failed</p>
        </>
      ) : (
        <>
          <p className="mt-2 text-heading-lg font-light tabular-nums text-text-primary">{value}</p>
          {sub && <div className="mt-0.5 text-body-xs text-text-tertiary">{sub}</div>}
        </>
      )}
    </Link>
  );
}

function MrrStat() {
  const subs = useSubscriptionsSummary();
  const mrr = useMrrOverview();
  const s = subs.data?.summary;
  const month = mrr.data?.overview.currentMonth;
  // The same four movements Finance's Net new MRR tile adds up.
  const netNew = month ? month.newMrr + month.expansionMrr - month.contractionMrr - month.churnedMrr : null;
  const overrides = s ? overrideSummary(s) : null;
  const nameOf = (customer: string) => s?.customerNames[customer]?.name ?? customer;
  const line =
    netNew === null ? null : mrrMovementLine(netNew, overrides && overrides.moves.length > 0 ? overrides.net : null, thisMonth());
  const sub = mrr.error ? (
    'Net new unavailable'
  ) : line ? (
    // One line; the split into new business and sheet rates is in the tooltip.
    line.headline
  ) : mrr.isLoading ? (
    'Net new…'
  ) : undefined;
  return (
    <TodayStat
      label="MRR"
      meta={`Odoo + MRR sheet · ${thisMonth()}`}
      href="/finance/overview"
      state={{ loading: subs.isLoading, error: subs.error }}
      value={s ? formatMoneyCompact(s.currentMrr) : undefined}
      title={
        s
          ? [
              formatMoney(Math.round(s.currentMrr)),
              netNew !== null && overrides && overrides.moves.length > 0
                ? `Net new in ${thisMonth()} to date ${signedMoney(netNew)}: new business ${signedMoney(netNew - overrides.net)}, sheet rates ${signedMoney(overrides.net)}.`
                : null,
              overrides && overrides.moves.length > 0
                ? `Sheet rates: rate changes in the MRR sheet while Odoo's contracts stood still. Rate restored ${signedMoney(overrides.restored)}, override changed ${signedMoney(overrides.changed)} (${overrides.moves.map((n) => nameOf(n.customer)).join(', ')}).`
                : null,
            ]
              .filter(Boolean)
              .join('\n')
          : undefined
      }
      sub={sub}
    />
  );
}

function RenewalsStat() {
  const subs = useSubscriptionsSummary();
  const n = subs.data ? contractsEnding(subs.data.summary.contracts, RENEWAL_WINDOW_DAYS).length : undefined;
  return (
    <TodayStat
      label="Renewals"
      meta={`Odoo · next ${RENEWAL_WINDOW_DAYS} days`}
      href="/finance/overview"
      state={{ loading: subs.isLoading, error: subs.error }}
      value={n}
      sub={n === undefined ? undefined : `${n === 1 ? 'contract ends' : 'contracts end'} in ${RENEWAL_WINDOW_DAYS} days`}
    />
  );
}

function OverdueInvoicesStat({ href }: { href: string }) {
  const inv = useInvoicesSummary('all');
  const s = inv.data?.summary;
  return (
    <TodayStat
      label="Overdue invoices"
      meta="Odoo · today"
      href={href}
      state={{ loading: inv.isLoading, error: inv.error }}
      value={s ? formatMoneyCompact(s.overdueTotal) : undefined}
      title={s ? `Overdue invoices: ${formatMoney(Math.round(s.overdueTotal))}` : undefined}
      sub={s ? `${s.overdueInvoices.length} invoice${s.overdueInvoices.length === 1 ? '' : 's'} past due` : undefined}
    />
  );
}

function PipelineStat() {
  // Same filter as Sales → Pipeline so the two share one cache entry. Active
  // and stale pipeline are "as of today" whatever the filter.
  const q = useSalesOverview({ quarters: [currentQuarter()], from: null, to: null });
  const t = q.data?.totals;
  const staleDays = q.data?.staleAfterDays;
  return (
    <TodayStat
      label="Active pipeline"
      meta="Attio · today"
      href="/sales/pipeline"
      state={{ loading: q.isLoading, error: q.error }}
      value={t ? formatMoneyCompact(t.activeValueEur) : undefined}
      title={
        t
          ? `Active: ${formatMoney(Math.round(t.activeValueEur))} in ${plural(t.activeCount, 'deal')} whose stage moved or whose account had an email or meeting in the last ${staleDays} days.\nStale: ${formatMoney(Math.round(t.staleValueEur))} in ${plural(t.staleCount, 'deal')} with no stage change and no account activity for ${staleDays}+ days, so not counted in active.\nAll open: ${formatMoney(Math.round(t.openValueEur))}.`
          : undefined
      }
      // The deals are the active ones; stale value is on top of the headline,
      // never part of it. What "stale" means is in the tooltip.
      sub={t ? `${plural(t.activeCount, 'active deal')} · ${formatMoneyCompact(t.staleValueEur)} stale` : undefined}
    />
  );
}

function TodosStat() {
  const q = useTaskCounts();
  const c = q.data?.counts;
  return (
    <TodayStat
      label="Overdue to-dos"
      meta="Organizer · today"
      href="/organizer"
      state={{ loading: q.isLoading, error: q.error }}
      value={c ? c.overdue : undefined}
      sub={c ? `${c.dueToday} due today` : undefined}
    />
  );
}

/**
 * Home's "Today" panel: a row of compact numbers, each gated on the area the
 * viewer can open and linking to it, then a "This week" strip: prospect
 * meetings, renewals by name, and (admins) what is broken.
 */
export function TodayPanel() {
  const { canAccess, canAccessArea, isAdmin } = useAccess();

  const showFinance = canAccessArea('finance');
  const showPipeline = canAccess('sales.ae');
  const showTodos = canAccessArea('organizer');
  const overdueHref = canAccess('finance.customers') ? '/finance/customers' : '/finance/overview';

  const stats: ReactNode[] = [];
  if (showFinance) stats.push(<MrrStat key="mrr" />, <OverdueInvoicesStat key="overdue" href={overdueHref} />);
  if (showPipeline) stats.push(<PipelineStat key="pipeline" />);
  if (showTodos) stats.push(<TodosStat key="todos" />);
  if (showFinance) stats.push(<RenewalsStat key="renewals" />);

  const showMeetings = canAccess('sales.ae');
  const hasWeek = showMeetings || showFinance || isAdmin;

  if (stats.length === 0 && !hasWeek) return null;

  return (
    <Card padding="sm" className="mt-8">
      <p className="eyebrow px-2">Today</p>
      {stats.length > 0 && (
        <div
          className={clsx(
            'mt-2 grid grid-cols-2 gap-x-4 gap-y-1 px-1',
            // Even columns: MRR's movement goes on two short lines instead of one wide one.
            stats.length >= 5 ? 'md:grid-cols-5' : stats.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3',
          )}
        >
          {stats}
        </div>
      )}
      {hasWeek && (
        <div className="mt-3 border-t border-border-light px-2 pt-3">
          <p className="eyebrow">This week</p>
          <dl className="mt-2 space-y-1.5 text-body-sm">
            {showMeetings && <MeetingsLine />}
            {showFinance && <RenewalsLine />}
            {isAdmin && <NeedsAttention />}
          </dl>
        </div>
      )}
    </Card>
  );
}

/** One "This week" row: a fixed-width term, then the content. */
function WeekRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 items-baseline gap-3">
      <dt className="w-28 flex-none text-body-xs text-text-tertiary">{term}</dt>
      <dd className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">{children}</dd>
    </div>
  );
}

/** How long the Meetings line waits before it says the load is stuck. */
const MEETINGS_TIMEOUT_MS = 15_000;

/** True once `loading` has stayed true for `ms`; resets when loading stops. */
function useTimedOut(loading: boolean, ms: number): boolean {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const t = setTimeout(() => setTimedOut(true), ms);
    return () => clearTimeout(t);
  }, [loading, ms]);
  return timedOut;
}

/** Prospect meetings this week (Mon–Sun), the same query Sales › Meetings runs for this week. */
function MeetingsLine() {
  const monday = mondayOf(new Date());
  const q = useSalesMeetings({ from: monday.toISOString(), to: addWeeks(monday, 1).toISOString() });
  const timedOut = useTimedOut(q.isLoading, MEETINGS_TIMEOUT_MS);
  const meetings = q.data?.meetings ?? [];
  const held = meetings.filter((m) => m.held).length;
  const retry = (
    <button type="button" onClick={() => void q.refetch()} className="text-accent hover:underline">
      Retry
    </button>
  );
  return (
    <WeekRow term="Meetings">
      {q.isLoading && !timedOut ? (
        <span className="text-text-muted">Loading…</span>
      ) : q.isLoading ? (
        <span className="text-warning-dark">
          Didn’t load in {MEETINGS_TIMEOUT_MS / 1000} s (Attio meetings mirror) · {retry}
        </span>
      ) : q.error ? (
        <span className="text-error-dark">
          Unavailable: {errorMessage(q.error)} · {retry}
        </span>
      ) : (
        <Link to="/sales/meetings?view=prospects" className="text-text-primary hover:underline">
          {plural(meetings.length, 'prospect meeting')}
          <span className="text-text-tertiary">
            {' '}
            · {held} held, {meetings.length - held} to come
          </span>
        </Link>
      )}
    </WeekRow>
  );
}

/** Contracts ending in the next 7 days by name; else the next one within 30 days. Same end dates as Finance. */
function RenewalsLine() {
  const subs = useSubscriptionsSummary();
  const upcoming = contractsEnding(subs.data?.summary.contracts ?? [], RENEWAL_WINDOW_DAYS);
  const thisWeek = upcoming.filter((c) => c.daysLeft! <= 7);
  const next = upcoming[0];
  const name = (c: (typeof upcoming)[number]) => `${c.companyName} ${formatDayMonth(c.expiryDate!)}`;
  return (
    <WeekRow term="Renewals">
      {subs.isLoading ? (
        <span className="text-text-muted">Loading…</span>
      ) : subs.error ? (
        <span className="text-text-tertiary" title={errorMessage(subs.error)}>
          Unavailable
        </span>
      ) : thisWeek.length > 0 ? (
        <Link to="/finance/overview" className="text-text-primary hover:underline">
          {thisWeek.map(name).join(' · ')}
        </Link>
      ) : (
        <span className="text-text-tertiary">
          None this week
          {next && (
            <>
              {' · next: '}
              <Link to="/finance/overview" className="text-text-primary hover:underline">
                {name(next)}
              </Link>
            </>
          )}
        </span>
      )}
    </WeekRow>
  );
}

/**
 * One line instead of two rows of status tiles: what is broken, by name,
 * linking to the page that fixes it. Integration status comes from the same
 * derived health the Integrations page and the sidebar footer read.
 */
function NeedsAttention() {
  const { exceptions, error: integrationsError, isLoading: intLoading } = useIntegrationExceptions();
  const { data: automationsData, error: automationsError, isLoading: autoLoading } = useAutomations();

  // The same count the Automations tile shows: failed, stuck and missed slots.
  const attention = attentionCounts(automationsData?.automations ?? []);

  const items: { key: string; label: string; href: string; title?: string }[] = [];
  if (integrationsError) items.push({ key: 'int', label: 'Integration status unavailable', href: '/integrations' });
  else if (exceptions.length > 0) {
    // Name up to two; the rest are counted.
    for (const e of exceptions.slice(0, 2)) {
      items.push({
        key: e.key,
        label: `${e.name} ${e.health.state === 'stuck' ? 'stuck' : 'failing'}`,
        href: '/integrations',
        title: e.health.reason ? failureCause(e.health.reason) : undefined,
      });
    }
    if (exceptions.length > 2)
      items.push({ key: 'int-more', label: `${exceptions.length - 2} more integration${exceptions.length - 2 === 1 ? '' : 's'}`, href: '/integrations' });
  }
  if (automationsError) items.push({ key: 'auto', label: 'Automation status unavailable', href: '/automations' });
  else if (attention.total > 0)
    items.push({
      key: 'auto',
      label: `${attention.total === 1 ? '1 automation' : `${attention.total} automations`}: ${attentionParts(attention).join(' · ')}`,
      href: '/automations',
      title: 'Failed = the last run failed. Stuck = a run started over an hour ago and never finished. Missed = no run finished in its latest scheduled slot (not a failure).',
    });

  // Reserve the line while loading so the card does not grow under the reader.
  if (intLoading || autoLoading) {
    return (
      <WeekRow term="Needs attention">
        <span className="text-text-muted" aria-busy>
          Checking integrations and automations…
        </span>
      </WeekRow>
    );
  }

  return (
    <WeekRow term="Needs attention">
      {items.length === 0 ? (
        <span className="flex items-center gap-2 text-text-tertiary">
          <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden />
          All integrations and automations running
        </span>
      ) : (
        <>
          <AlertTriangle className="h-4 w-4 flex-none self-center text-error" aria-hidden />
          {items.map((item, i) => (
            <span key={item.key} className="flex items-center gap-2">
              {i > 0 && <span className="text-text-muted">·</span>}
              <Link
                to={item.href}
                title={item.title}
                className="inline-flex items-center gap-1 font-medium text-text-primary hover:underline"
              >
                {item.label}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </span>
          ))}
        </>
      )}
    </WeekRow>
  );
}
